import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Target, Clock, Package, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const FigureBMWArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-blue-500 text-white">
                <Target className="h-3 w-3 mr-1" />
                Automation
              </Badge>
              <span className="text-gray-500 text-sm">November 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Figure 02 Robot Completes Historic 11-Month Deployment at BMW Production Line
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Figure AI's humanoid robot achieves unprecedented success in automotive manufacturing, 
              demonstrating the viability of humanoid robotics in industrial applications.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://images.ctfassets.net/qx5k8y1u9drj/1rgCWPZcqd52zB9viPKmcF/4a570687605c87edc08992fb83153365/Hands-Photo_Update.jpg?fm=webp&w=1920&q=70" 
              alt="Figure 02 humanoid robot hands demonstrating precision manipulation" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                  Parts Loaded
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">90,000+</div>
                <p className="text-sm text-gray-600">Sheet-metal components</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  Runtime
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">1,250+</div>
                <p className="text-sm text-gray-600">Operational hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                  Vehicles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">30,000+</div>
                <p className="text-sm text-gray-600">BMW X3 vehicles produced</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Deployment Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Figure AI's groundbreaking deployment at BMW Group Plant Spartanburg represents a watershed moment 
              for humanoid robotics in manufacturing. Over 11 months, the Figure 02 robot successfully integrated 
              into an active automotive production line, performing sheet-metal loading operations with remarkable 
              consistency and precision.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Technical Achievement</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The deployment focused on a classic pick-and-place task where the robot retrieved sheet-metal parts 
              from racks and bins, placing them on welding fixtures with millimeter precision. This required the 
              robot to achieve placement accuracy within a 5-millimeter tolerance in just 2 seconds, balancing 
              speed with exceptional precision.
            </p>

            <div className="mb-8">
              <video 
                className="w-full h-auto rounded-lg shadow-lg" 
                controls 
                preload="metadata"
                poster="https://images.ctfassets.net/qx5k8y1u9drj/1rgCWPZcqd52zB9viPKmcF/4a570687605c87edc08992fb83153365/Hands-Photo_Update.jpg?fm=webp&w=1920&q=70"
              >
                <source src="https://videos.ctfassets.net/qx5k8y1u9drj/2fhO8Txwv7qK3hSxtyANqe/e6b814d2e72dcb12d4d9219b4d672247/BMW_Clip_Wide.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Figure 02 robot performing precision sheet-metal loading operations at BMW Group Plant Spartanburg
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Performance Metrics</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <ul className="space-y-2">
                <li><strong>Cycle Time:</strong> 84 seconds total (37 seconds load time)</li>
                <li><strong>Placement Accuracy:</strong> &gt;99% success rate per shift</li>
                <li><strong>Interventions:</strong> Minimal human intervention required</li>
                <li><strong>Operational Schedule:</strong> 10-hour shifts, Monday through Friday</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation Highlights</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The success required breakthrough developments in several key areas:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Adaptive Locomotion:</strong> Precise foot placement and real-time environmental responsiveness</li>
              <li><strong>Hand-Eye Coordination:</strong> Advanced algorithms enabling rapid, accurate manipulation</li>
              <li><strong>Field Calibration:</strong> Tools ensuring consistent performance across robot units</li>
              <li><strong>Reliability Engineering:</strong> Robust design for continuous industrial operation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Lessons for Figure 03</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The deployment provided crucial insights that directly informed the development of Figure 03. 
              Key learnings included forearm reliability improvements, where the team completely re-architected 
              the wrist electronics to eliminate distribution boards and dynamic cabling, significantly improving 
              reliability and thermal management.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Impact</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              This deployment demonstrates that humanoid robots can successfully transition from laboratory 
              environments to real-world manufacturing applications. The achievement validates the potential 
              for humanoid robotics to address labor shortages and enhance manufacturing efficiency while 
              working alongside human operators.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on official announcement from Figure AI regarding their BMW Group Plant Spartanburg deployment. 
                <a href="https://www.figure.ai/news/production-at-bmw" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default FigureBMWArticle;